// Stock d'armatures (cages) par type de poteau : inventaire, armatures fabriquées et poteaux coulés (rapports journaliers).
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { DeleteOutline, FactCheckOutlined, HistoryOutlined } from '@mui/icons-material';
import Layout from '../components/Layout';
import { Panel } from '../components/ui';
import { armaturesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { apiError, dateFr, dateHeureFr, today } from '../utils/format';
import { tokens } from '../theme';

const StockArmatures = () => {
  const { isAdmin, hasRole } = useAuth();
  const resp = isAdmin || hasRole('sous_admin');
  const [d, setD] = useState(null);
  const [inv, setInv] = useState(null); // { date, commentaire, q: {} }
  const [hist, setHist] = useState(null);
  const [info, setInfo] = useState('');
  const [error, setError] = useState('');
  const charger = useCallback(() => armaturesAPI.etat().then((r) => setD(r.data)).catch((e) => setError(apiError(e))), []);
  useEffect(() => { charger(); }, [charger]);

  const enregistrer = async () => {
    setError('');
    try {
      const r = await armaturesAPI.inventaire({ date: inv.date, commentaire: inv.commentaire, lignes: Object.entries(inv.q).map(([pba_type, quantite]) => ({ pba_type, quantite })) });
      setInv(null); setInfo(r.data.message); charger();
    } catch (e) { setError(apiError(e)); }
  };
  const ouvrirHist = () => armaturesAPI.inventaires().then((r) => setHist(r.data)).catch((e) => setError(apiError(e)));
  const supprimer = async (h) => {
    if (!window.confirm(`Supprimer l'inventaire ${h.pba_type} du ${dateFr(h.date)} (${h.quantite}) ?`)) return;
    try { await armaturesAPI.supprimer(h.id); ouvrirHist(); charger(); } catch (e) { setError(apiError(e)); }
  };
  const nonInit = (d || []).filter((r) => !r.initialise);

  return (
    <Layout title="Stock d'armatures" plain
      actions={<>
        <Button startIcon={<HistoryOutlined />} onClick={ouvrirHist}>Inventaires</Button>
        {resp && <Button variant="contained" startIcon={<FactCheckOutlined />} onClick={() => setInv({ date: today(), commentaire: '', q: {} })}>Inventaire des armatures</Button>}
      </>}>
      {info && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo('')}>{info}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {d && nonInit.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Faites un premier inventaire des armatures en stock pour démarrer le suivi{nonInit.length < d.length ? ` (types sans inventaire : ${nonInit.map((r) => r.pba_type).join(', ')})` : ''}.
          Ensuite, chaque rapport journalier ajoute les armatures fabriquées et retire celles des poteaux coulés.
        </Alert>
      )}
      <Panel flush>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell align="right">Stock</TableCell>
                <TableCell>Dernier inventaire</TableCell>
                <TableCell align="right">+ Fabriquées</TableCell>
                <TableCell align="right">− Coulées</TableCell>
                <TableCell align="right">Fabriquées / coulées (30 j)</TableCell>
                <TableCell align="right">Couverture</TableCell>
                <TableCell align="right">Planifié 7 j</TableCell>
                <TableCell align="right">À fabriquer</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {d?.map((r) => (
                <TableRow key={r.pba_type} hover>
                  <TableCell sx={{ fontWeight: 700 }}>{r.pba_type}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: tokens.mono, fontWeight: 700, fontSize: 15, color: r.stock !== null && r.stock < 0 ? 'error.main' : undefined }}>{r.stock ?? '—'}</TableCell>
                  <TableCell sx={{ fontSize: 13 }}>{r.initialise ? `${r.inventaire} le ${dateFr(r.inventaire_date)}` : <Chip size="small" variant="outlined" label="à faire" />}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: tokens.mono }}>{r.initialise ? r.fabriquees : '—'}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: tokens.mono }}>{r.initialise ? r.consommees : '—'}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: tokens.mono, color: 'text.secondary' }}>{r.fab_30j} / {r.conso_30j}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: tokens.mono }}>{r.couverture_jours !== null ? `${String(r.couverture_jours).replace('.', ',')} j` : '—'}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: tokens.mono }}>{r.prevu_7j || '—'}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: tokens.mono, fontWeight: r.a_fabriquer_7j ? 700 : 400, color: r.a_fabriquer_7j ? 'warning.main' : 'text.disabled' }}>{r.a_fabriquer_7j || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Panel>
      <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 1 }}>
        Stock = dernier inventaire + armatures fabriquées − poteaux coulés (rapports journaliers postérieurs à l'inventaire). Couverture = stock ÷ poteaux coulés par jour (moyenne 30 j).
        « À fabriquer » = production planifiée des 7 prochains jours non couverte par le stock.
      </Typography>

      <Dialog open={!!inv} onClose={() => setInv(null)} fullWidth maxWidth="sm">
        <DialogTitle>Inventaire des armatures</DialogTitle>
        <DialogContent dividers>
          {inv && (
            <>
              <Typography sx={{ fontSize: 13.5, color: 'text.secondary', mb: 2 }}>Comptez les armatures prêtes (non coulées) en fin de journée. Seuls les types saisis sont mis à jour.</Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField type="date" label="Date du comptage" InputLabelProps={{ shrink: true }} value={inv.date} inputProps={{ max: today() }} onChange={(e) => setInv({ ...inv, date: e.target.value })} />
                <TextField label="Commentaire" value={inv.commentaire} onChange={(e) => setInv({ ...inv, commentaire: e.target.value })} sx={{ flex: 1 }} />
              </Box>
              <Table size="small">
                <TableBody>
                  {d.map((r) => (
                    <TableRow key={r.pba_type}>
                      <TableCell sx={{ fontWeight: 700 }}>{r.pba_type}</TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: 13 }}>théorique : {r.stock ?? '—'}</TableCell>
                      <TableCell align="right">
                        <TextField variant="standard" value={inv.q[r.pba_type] ?? ''} placeholder="—" sx={{ width: 90 }}
                          inputProps={{ inputMode: 'numeric', style: { textAlign: 'right' }, 'aria-label': `Armatures ${r.pba_type}` }}
                          onChange={(e) => { const v = e.target.value.replace(/[^\d]/g, ''); setInv((x) => { const q = { ...x.q }; if (v === '') delete q[r.pba_type]; else q[r.pba_type] = v; return { ...x, q }; }); }} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setInv(null)}>Annuler</Button><Button variant="contained" disabled={!inv || !Object.keys(inv.q).length} onClick={enregistrer}>Enregistrer</Button></DialogActions>
      </Dialog>

      <Dialog open={!!hist} onClose={() => setHist(null)} fullWidth maxWidth="sm">
        <DialogTitle>Inventaires des armatures</DialogTitle>
        <DialogContent dividers>
          {hist?.length ? (
            <Table size="small">
              <TableHead><TableRow><TableCell>Date</TableCell><TableCell>Type</TableCell><TableCell align="right">Compté</TableCell><TableCell>Saisi par</TableCell><TableCell /></TableRow></TableHead>
              <TableBody>{hist.map((h) => (
                <TableRow key={h.id}>
                  <TableCell>{dateFr(h.date)}</TableCell><TableCell>{h.pba_type}</TableCell><TableCell align="right">{h.quantite}</TableCell>
                  <TableCell sx={{ fontSize: 12.5 }}>{h.saisi_par}<Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>{dateHeureFr(h.created_at)}{h.commentaire ? ` · ${h.commentaire}` : ''}</Typography></TableCell>
                  <TableCell>{resp && <Tooltip title="Supprimer"><IconButton size="small" color="error" aria-label="Supprimer l'inventaire" onClick={() => supprimer(h)}><DeleteOutline fontSize="small" /></IconButton></Tooltip>}</TableCell>
                </TableRow>
              ))}</TableBody>
            </Table>
          ) : <Typography sx={{ color: 'text.secondary' }}>Aucun inventaire.</Typography>}
        </DialogContent>
        <DialogActions><Button onClick={() => setHist(null)}>Fermer</Button></DialogActions>
      </Dialog>
    </Layout>
  );
};

export default StockArmatures;
